import cloneDeep from "lodash/cloneDeep";

/** SFAM `formData` row ids we own on getting-started + POST merge. */
export const DOMAIN_FORM_GETTING_STARTED_IDS = {
  businessName: "businessName",
  businessDescription: "businessDescription",
  businessUnique: "businessUnique",
};

const GETTING_STARTED_FIELD_SPECS = [
  {
    id: DOMAIN_FORM_GETTING_STARTED_IDS.businessName,
    defaultLabel: "Project Title or Business Name",
    websiteKey: "title",
  },
  {
    id: DOMAIN_FORM_GETTING_STARTED_IDS.businessDescription,
    defaultLabel: "What's your domain about?",
    websiteKey: "category",
  },
  {
    id: DOMAIN_FORM_GETTING_STARTED_IDS.businessUnique,
    defaultLabel:
      "Describe your brand or business in a few sentences - what makes it unique?",
    websiteKey: "prompt",
  },
];

/**
 * Map GET `formData` answers into getting-started `websiteDetails` fields (title / category / prompt).
 * Only includes keys where a non-empty answer exists.
 *
 * @param {unknown[]} formData
 * @returns {{ title?: string, category?: string, prompt?: string }}
 */
export function formDataToWebsiteDetailsPatch(formData) {
  if (!Array.isArray(formData)) return {};
  const out = {};
  for (const spec of GETTING_STARTED_FIELD_SPECS) {
    const row = formData.find((r) => r?.id === spec.id);
    const answer = row?.answer;
    if (answer != null && String(answer).trim() !== "") {
      out[spec.websiteKey] = String(answer);
    }
  }
  return out;
}

/**
 * Prefill only keys that are still empty on `prevWebsite`.
 *
 * @param {{ title?: string, category?: string, prompt?: string }} prevWebsite
 * @param {{ title?: string, category?: string, prompt?: string }} patch
 */
export function applyWebsitePrefillFromFormData(prevWebsite, patch) {
  const next = { ...prevWebsite };
  for (const [k, v] of Object.entries(patch)) {
    const cur = prevWebsite[k];
    if (cur == null || String(cur).trim() === "") {
      if (v != null && String(v).trim() !== "") {
        next[k] = String(v);
      }
    }
  }
  return next;
}

/**
 * Merge current getting-started answers into a clone of the GET snapshot: upserts our three
 * `formData` rows and keeps every other row from the GET (other apps' custom fields).
 *
 * @param {object} snapshot — full GET payload (`prodInstanceId`, `domainName`, `formData`, …)
 * @param {{ title?: string, category?: string, prompt?: string }} websiteDetails
 */
export function mergeGettingStartedIntoDomainFormSnapshot(snapshot, websiteDetails) {
  const base = cloneDeep(snapshot);
  if (!Array.isArray(base.formData)) {
    base.formData = [];
  }
  const byId = new Map(base.formData.map((row) => [row.id, { ...row }]));

  for (const spec of GETTING_STARTED_FIELD_SPECS) {
    const raw = websiteDetails[spec.websiteKey];
    const answer = raw == null ? "" : String(raw);
    const prev = byId.get(spec.id);
    byId.set(spec.id, {
      id: spec.id,
      label: prev?.label ?? spec.defaultLabel,
      answer,
    });
  }

  base.formData = Array.from(byId.values());
  return base;
}
